/**
 * Google Maps JS SDK loader (F-FE-N26).
 *
 * Singleton script-tag injector: the SDK is never in the app bundle and only
 * loads on demand (the Delivery Zones editor). Re-invocations — including
 * remounts of the page — reuse the same in-flight promise, so the script tag
 * is injected at most once per session. A key change (admin pasted a new key)
 * requires a reload; we surface that instead of double-injecting.
 *
 * This is the plain <script> loader (no npm runtime dep) — the sanctioned,
 * TOS-clean way to consume Maps JS. Types come from @types/google.maps.
 */

let loading: Promise<typeof google.maps> | null = null
let loadedKey: string | null = null

// A rejected key (wrong, unbilled, referrer not allowed, API not enabled)
// still loads the script — Google greys the map out and calls
// window.gm_authFailure. Surface it so the admin sees why, instead of a dead map.
let authFailed = false
const authListeners = new Set<() => void>()
if (typeof window !== 'undefined') {
  ;(window as any).gm_authFailure = () => {
    authFailed = true
    authListeners.forEach((cb) => cb())
  }
}

/** Subscribe to Google rejecting the key; fires at once if it already has. */
export function onMapsAuthFailure(cb: () => void): () => void {
  if (authFailed) cb()
  authListeners.add(cb)
  return () => {
    authListeners.delete(cb)
  }
}

export function loadGoogleMaps(apiKey: string): Promise<typeof google.maps> {
  const key = apiKey.trim()
  if (!key) return Promise.reject(new Error('Maps aren’t switched on for this restaurant yet'))
  if (loading) {
    if (loadedKey !== key) {
      return Promise.reject(
        new Error('Google Maps was already loaded with a different API key — reload the page to apply the new key.'),
      )
    }
    return loading
  }
  loadedKey = key
  loading = new Promise((resolve, reject) => {
    if (typeof google !== 'undefined' && google.maps?.Map) {
      resolve(google.maps)
      return
    }
    const cbName = '__posGmapsLoaded'
    ;(window as any)[cbName] = () => {
      delete (window as any)[cbName]
      resolve(google.maps)
    }
    const script = document.createElement('script')
    // NOTE: no `drawing` library — Google removed DrawingManager from Maps
    // JS v3.65+; the zones editor draws polygons manually (map clicks).
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&libraries=marker&loading=async&callback=${cbName}`
    script.async = true
    script.onerror = () => {
      loading = null
      loadedKey = null
      reject(new Error('Could not load the map — check your connection.'))
    }
    document.head.appendChild(script)
  })
  return loading
}
